const FRANKFURTER_LATEST = "https://api.frankfurter.app/latest";
const FETCH_TIMEOUT_MS = 15_000;

/**
 * Converts `amount` from `fromCurrency` to `toCurrency` using Frankfurter (ECB reference rates).
 * Returns the original amount if both codes are the same (case-insensitive).
 */
export async function convertAmountToCurrency(
  amount: number,
  fromCurrency: string,
  toCurrency: string,
): Promise<number> {
  const from = fromCurrency.trim().toUpperCase();
  const to = toCurrency.trim().toUpperCase();
  if (from === to) return amount;
  if (!Number.isFinite(amount) || !(amount > 0)) {
    throw new Error("Invalid amount for conversion.");
  }

  const url = `${FRANKFURTER_LATEST}?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`;
  const ctrl = new AbortController();
  const timer = window.setTimeout(() => ctrl.abort(), FETCH_TIMEOUT_MS);
  try {
    const res = await fetch(url, { signal: ctrl.signal });
    if (!res.ok) {
      throw new Error(
        `Could not load exchange rates (${res.status}). Check your connection or enter the amount in ${to}.`,
      );
    }
    const data = (await res.json()) as { rates?: Record<string, number> };
    const rate = data.rates?.[to];
    if (typeof rate !== "number" || !Number.isFinite(rate)) {
      throw new Error(
        `No rate for ${from} → ${to}. Try another currency or enter the amount in ${to}.`,
      );
    }
    const converted = amount * rate;
    return Math.round(converted * 100) / 100;
  } catch (e) {
    if (e instanceof Error && e.name === "AbortError") {
      throw new Error(
        `Exchange rate request timed out. Enter the amount in ${to} or try again.`,
      );
    }
    throw e;
  } finally {
    window.clearTimeout(timer);
  }
}

/**
 * For each distinct `from` in `fromCurrencies`, returns a multiplier such that
 * `amountInFrom * mult.get(from) === amountInDefault` (ECB reference rates via Frankfurter).
 * `defaultCurrency` always maps to `1`.
 */
export async function buildToDefaultMultipliers(
  fromCurrencies: Iterable<string>,
  defaultCurrency: string,
): Promise<Map<string, number>> {
  const target = defaultCurrency.trim().toUpperCase();
  const mult = new Map<string, number>();
  mult.set(target, 1);
  const seen = new Set<string>();
  for (const raw of fromCurrencies) {
    const c = raw.trim().toUpperCase();
    if (!c || seen.has(c)) continue;
    seen.add(c);
    if (c === target) continue;
    mult.set(c, await convertAmountToCurrency(1, c, target));
  }
  return mult;
}
